/**
 * Pure physics functions for the PvP roulette wheel engine.
 * No Pixi imports, no `this`, no Date.now() / performance.now() calls.
 * All timing is passed as explicit parameters so functions are fully testable in Node.
 */

// ─── Velocity: SPINNING / TRICKS ────────────────────────────────────────────

/** Snapshot of the engine fields needed to compute spinning/tricks velocity. */
export interface SpinningVelocityState {
  /** performance.now() at the moment spinning started (>0 when SPINNING). */
  spinStartedAt: number
  /** Total SPINNING phase duration in ms. */
  spinDurationMs: number
  /** performance.now() at the moment TRICKS started (>0 when in TRICKS). */
  tricksPhaseStartTime: number
  /** Date.now() at the moment TRICKS started (wall-clock for decay alignment). */
  tricksPhaseStartTimeWallMs: number
  /** Total TRICKS window duration in ms (wall-clock interval). */
  tricksDurationMs: number
  /** Velocity at the moment TRICKS started (deg/ms, signed). */
  tricksEntryVelDegPerMs: number
}

export interface SpinningVelocityParams {
  state: SpinningVelocityState
  /** Current performance.now() — used for SPINNING ramp/decay. */
  nowPerfMs: number
  /** Current Date.now() — used only in TRICKS branch for wall-clock elapsed. */
  nowWallMs: number
  /** WHEEL_VISUAL_SPIN_SIGN: +1 CW, -1 CCW. */
  spinSign: number
  /** SPINNING_PEAK_REV_PER_SEC. */
  peakRevPerSec: number
  /** TRICKS_START_REV_PER_SEC — target velocity at SPINNING→TRICKS transition. */
  tricksStartRevPerSec: number
  /** TRICKS_END_REV_PER_SEC — velocity at end of TRICKS. */
  tricksEndRevPerSec: number
  /** SPINNING_RAMP_UP_MS. */
  rampUpMs: number
  /** SPINNING_RAMP_EASE_EXP — exponent for `1-(1-u)^exp` ramp curve. */
  rampEaseExp: number
  /** TRICKS_DECAY_EXPONENT — exponent for `t^exp` TRICKS decay curve. */
  tricksDecayExp: number
}

/**
 * Velocity (deg/ms, signed) during SPINNING or TRICKS phase.
 *
 * SPINNING: ease-in ramp over `rampUpMs`, then linear decay to `tricksStartRevPerSec`.
 * TRICKS:   power-curve decay from `tricksEntryVelDegPerMs` → `tricksEndRevPerSec`.
 * IDLE:     0 (neither phase active).
 */
export function computeSpinningVelocity(
  params: SpinningVelocityParams
): number {
  const {
    state,
    nowPerfMs,
    nowWallMs,
    spinSign: s,
    peakRevPerSec,
    tricksStartRevPerSec,
    tricksEndRevPerSec,
    rampUpMs,
    rampEaseExp,
    tricksDecayExp,
  } = params

  const peakVel = (peakRevPerSec * 360) / 1000
  const tricksStartVel = (tricksStartRevPerSec * 360) / 1000
  const tricksEndVel = (tricksEndRevPerSec * 360) / 1000
  const signedTricksEnd = s * tricksEndVel

  // ── TRICKS phase ──────────────────────────────────────────────────────────
  // Uses wall-clock elapsed so tricksDurationMs (also wall-clock) aligns correctly.
  if (state.tricksPhaseStartTime > 0) {
    if (state.tricksDurationMs > 0) {
      const wallElapsed = nowWallMs - state.tricksPhaseStartTimeWallMs
      const t = Math.min(1, Math.max(0, wallElapsed / state.tricksDurationMs))
      const p = Math.pow(t, tricksDecayExp)
      return (
        state.tricksEntryVelDegPerMs +
        (signedTricksEnd - state.tricksEntryVelDegPerMs) * p
      )
    }
    return state.tricksEntryVelDegPerMs
  }

  // ── SPINNING phase ────────────────────────────────────────────────────────
  if (state.spinStartedAt > 0) {
    const elapsed = nowPerfMs - state.spinStartedAt
    if (elapsed < rampUpMs) {
      const u = elapsed / rampUpMs
      const rampMag = 1 - Math.pow(1 - u, rampEaseExp)
      return s * peakVel * rampMag
    }
    const decayTotal = state.spinDurationMs - rampUpMs
    if (decayTotal > 0) {
      const t = Math.min(1, (elapsed - rampUpMs) / decayTotal)
      return s * (peakVel + (tricksStartVel - peakVel) * t)
    }
    return s * tricksStartVel
  }

  return 0
}

// ─── Finalize solver ─────────────────────────────────────────────────────────

export interface SolveFinalizeDeltaParams {
  /** Current angular velocity (deg/ms, signed). */
  velDegPerMs: number
  /** SLOWING phase duration in ms. */
  durationMs: number
  /** Target angle (degrees, [0..360)). */
  targetAngleDeg: number
  /** Current angle (degrees, can be any positive value due to accumulated rotations). */
  currentAngleDeg: number
  /** WHEEL_VISUAL_SPIN_SIGN: +1 or -1 — determines rotation direction. */
  spinSign: number
  /** SLOWING_TARGET_EXP — desired ease-out exponent. */
  targetExp: number
}

export interface SolveFinalizeDeltaResult {
  /** Signed rotation distance (degrees) including extra full turns. */
  deltaDeg: number
  /**
   * Entry-side exponent — matches `v(0)` to the actual entry velocity `v0` so the
   * SLOWING phase **never** introduces a velocity discontinuity (jolt) at the
   * TRICKS→SLOWING transition. Computed as `rawExp = |v0|*T/|delta|`, clamped
   * only to ≥ 1.0 to keep `(1-t)^(expEntry-1)` numerically stable as t→1.
   */
  expEntry: number
  /**
   * Tail-side exponent — controls the shape of the ease-out near `t→1` so the
   * wheel always lands smoothly on target without a "wall hit". Lower bound is
   * `SLOWING_TARGET_EXP` (нет «снапа» на финише).
   */
  expTail: number
}

/**
 * Порог `natural_exp = v0·T/|delta|`, выше которого одна-экспоненциальная кривая уже
 * монотонно тормозит без необходимости в crossfade. Когда rawExp ≥ этого порога, мы
 * возвращаем `expEntry === expTail = rawExp` — одно-экспоненциальный ease-out, в котором
 * скорость **строго монотонно** убывает от v0 до 0. Никакого «бугра» в середине,
 * никакого скачка в начале.
 *
 * При rawExp < порога монотонное торможение **математически невозможно**
 * (v0·T < delta — не хватает момента, чтобы добраться до target без разгона), поэтому
 * включается crossfade: expEntry удерживает v(0)=v0 точно, expTail тянет хвост к
 * targetExp. Бугор в середине неизбежен, но его магнитуду минимизирует выбор k=0
 * в loop'е выше.
 *
 * `1.2` — выбран так, чтобы хвост одно-экспоненциальной кривой при rawExp=1.2 ещё
 * выглядел плавно (`v(0.99) ≈ 0.48 · delta/T`, последний кадр падает на доли градуса).
 */
const SINGLE_CURVE_THRESHOLD = 1.2

/**
 * Choose the number of extra full rotations `k` that minimise
 * `|velDegPerMs * durationMs / delta - targetExp|`,
 * then compute the signed `deltaDeg` and the resulting ease-out `expEntry/expTail`.
 *
 * The two-exponent crossfade position formula is:
 *   x(t) = startDeg + deltaDeg * f(t)
 *   f(t) = 1 − (1−w(t))·(1−t)^expEntry − w(t)·(1−t)^expTail
 *   w(t) = smootherstep(t) = 6t⁵ − 15t⁴ + 10t³
 *
 * `v(0) = expEntry · delta / T` and `v(1) = 0` (для expEntry, expTail > 0; см. computeSlowingVelocity).
 * Setting `expEntry = v0 · T / |delta|` makes the start velocity match v0 exactly (no jolt).
 * Setting `expTail ≥ SLOWING_TARGET_EXP` guarantees a smooth tail.
 *
 * Loop picks `k` so that `rawExp ≈ targetExp` to keep the entry shape close to the desired
 * deceleration profile when v0 is high; when v0 is low (TRICKS_END speed), crossfade absorbs
 * the mismatch via the smoother tail curve instead of clamping the entry exponent.
 */
export function solveFinalizeDelta(
  params: SolveFinalizeDeltaParams
): SolveFinalizeDeltaResult {
  const {
    velDegPerMs,
    durationMs,
    targetAngleDeg,
    currentAngleDeg,
    spinSign,
    targetExp,
  } = params

  const cur = ((currentAngleDeg % 360) + 360) % 360
  const base =
    spinSign >= 0
      ? (targetAngleDeg - cur + 360) % 360
      : (cur - targetAngleDeg + 360) % 360

  const idealDelta = (Math.abs(velDegPerMs) * durationMs) / targetExp
  const idealN = Math.max(0, Math.round((idealDelta - base) / 360))
  const kMax = Math.min(120, Math.max(idealN + 2, 2))

  let bestN = 0
  let bestDist = Infinity
  for (let k = 0; k <= kMax; k++) {
    const d = base + k * 360
    if (d <= 0) continue
    const e = (Math.abs(velDegPerMs) * durationMs) / d
    // Штрафуем e < 1 (не хватает момента для монотонного торможения с этой delta).
    // При наличии k с e ≥ 1 такой k предпочтительнее: одна экспонента, монотонно
    // убывающая скорость, без бугра. Когда ВСЕ k дают e < 1 (тяжёлый FREEZE), выбираем
    // тот, что ближе к 1 — это минимизирует магнитуду неизбежного бугра в crossfade.
    const dist = e < 1.0 ? 1e6 + (1.0 - e) : Math.abs(e - targetExp)
    if (dist < bestDist) {
      bestDist = dist
      bestN = k
    }
  }

  const delta = spinSign >= 0 ? base + bestN * 360 : -(base + bestN * 360)

  const ratio = delta !== 0 ? (velDegPerMs * durationMs) / delta : 0
  const rawExp = ratio > 0 && Number.isFinite(ratio) ? ratio : targetExp
  const safeRawExp =
    Number.isFinite(rawExp) && rawExp > 0 ? rawExp : targetExp

  // Если natural exp достаточно большой — момента хватает на монотонное торможение,
  // используем одну экспоненту: expEntry === expTail = natural. v(0) = v0 точно,
  // v(t) строго убывает, v(1) = 0. Ни скачка, ни бугра.
  if (safeRawExp >= SINGLE_CURVE_THRESHOLD) {
    return { deltaDeg: delta, expEntry: safeRawExp, expTail: safeRawExp }
  }

  // natural exp < порога — момента не хватает для монотонного торможения.
  // Crossfade: expEntry = natural (без clamp вверх → нет скачка v(0)),
  // expTail тянет хвост к targetExp для плавной посадки. Smootherstep гарантирует
  // C²-гладкость перехода, гасит сингулярность `(1−t)^(expEntry−1)` для expEntry < 1
  // (фактор `(1−w)` ведёт себя как `(1−t)³` к концу, см. computeSlowingVelocity).
  // Маленький бугор в середине неизбежен математически.
  return {
    deltaDeg: delta,
    expEntry: safeRawExp,
    expTail: Math.max(targetExp, safeRawExp),
  }
}

// ─── Slowing (crossfade-power-curve) position & velocity ─────────────────────

/** State of an active finalization (SLOWING phase). */
export interface FinalizeState {
  /** performance.now() when finalization started. */
  startedAt: number
  /** Total duration of the SLOWING phase (ms). */
  durationMs: number
  /** Wheel angle at finalization start (degrees, accumulated). */
  startDeg: number
  /** Signed rotation distance to cover (degrees). */
  deltaDeg: number
  /** Absolute target angle (startDeg + deltaDeg). */
  targetDeg: number
  /** Entry-side ease-out exponent — matches v(0) = v0 (no jolt at TRICKS→SLOWING). */
  expEntry: number
  /** Tail-side ease-out exponent — ≥ SLOWING_TARGET_EXP for smooth landing. */
  expTail: number
}

/**
 * Smootherstep blend: w(0)=0, w(1)=1, w'(0)=w'(1)=0, w''(0)=w''(1)=0.
 * Vanishes as `(1-t)^3` near t=1 — kills any `(1-t)^(expEntry-1)` singularity in the
 * entry term when expEntry happens to be < 1 due to numerical drift.
 */
function smootherstep(t: number): number {
  return t * t * t * (t * (t * 6 - 15) + 10)
}

/** d/dt of `smootherstep`: 30t²(1-t)². */
function smootherstepDeriv(t: number): number {
  const u = 1 - t
  return 30 * t * t * u * u
}

/**
 * Wheel angle at time `nowPerfMs` during SLOWING phase.
 *
 * Two-exponent crossfade so SLOWING starts at v0 (no jolt) and lands smoothly (no wall hit):
 *   f(t) = 1 − (1−w(t))·(1−t)^expEntry − w(t)·(1−t)^expTail,   w(t) = smootherstep(t)
 *   x(t) = startDeg + deltaDeg · f(t)
 *
 * При `expEntry === expTail` cross-fade схлопывается до стандартной `1 − (1−t)^exp`.
 */
export function computeSlowingPosition(
  state: FinalizeState,
  nowPerfMs: number
): number {
  const t = Math.min(
    1,
    Math.max(0, (nowPerfMs - state.startedAt) / state.durationMs)
  )
  if (t >= 1) return state.targetDeg
  return state.startDeg + state.deltaDeg * computeSlowingShape(t, state)
}

/**
 * Wheel angular velocity (deg/ms, signed) at time `nowPerfMs` during SLOWING phase.
 *
 * Аналитическая производная cross-fade-формулы:
 *   v(t) = deltaDeg/T · [ w'(t)·((1−t)^expEntry − (1−t)^expTail)
 *                       + (1−w(t))·expEntry·(1−t)^(expEntry−1)
 *                       + w(t)·expTail·(1−t)^(expTail−1) ]
 *
 * v(0) = deltaDeg/T · expEntry  (равно v0 при expEntry = v0·T/|delta|).
 * v(1) = 0  (для expEntry, expTail > 0; смуттерстеп ~(1−t)^3 покрывает крайний случай
 *            expEntry < 1, когда `(1−t)^(expEntry−1)` сам по себе расходится).
 */
export function computeSlowingVelocity(
  state: FinalizeState,
  nowPerfMs: number
): number {
  const t = Math.min(
    1,
    Math.max(0, (nowPerfMs - state.startedAt) / state.durationMs)
  )
  if (t >= 1) return 0
  const u = 1 - t
  const w = smootherstep(t)
  const wDeriv = smootherstepDeriv(t)
  const matchU = Math.pow(u, state.expEntry)
  const tailU = Math.pow(u, state.expTail)
  const matchSlope =
    state.expEntry > 0 ? state.expEntry * Math.pow(u, state.expEntry - 1) : 0
  const tailSlope =
    state.expTail > 0 ? state.expTail * Math.pow(u, state.expTail - 1) : 0
  const shapeDeriv =
    wDeriv * (matchU - tailU) + (1 - w) * matchSlope + w * tailSlope
  return (state.deltaDeg / state.durationMs) * shapeDeriv
}

/** Dimensionless cross-fade shape `f(t)` ∈ [0, 1] used inside `computeSlowingPosition`. */
function computeSlowingShape(t: number, state: FinalizeState): number {
  const u = 1 - t
  if (state.expEntry === state.expTail) {
    return 1 - Math.pow(u, state.expEntry)
  }
  const w = smootherstep(t)
  return 1 - (1 - w) * Math.pow(u, state.expEntry) - w * Math.pow(u, state.expTail)
}

// ─── Command deduplication ───────────────────────────────────────────────────

/** Canonical dedup key for a wheel command: `roomId:roundVersion:commandId`. */
export function makeCommandDedupKey(
  roomId: string,
  roundVersion: number,
  commandId: string
): string {
  return `${roomId}:${roundVersion}:${commandId}`
}
